import {Nav} from '@components/faqs/StyledFAQ';
import {Box, CircularProgress, Grid, Modal, Typography} from '@mui/material';
import {styled, useTheme} from '@mui/material/styles';
import axios from 'axios';
import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';

import {RootState} from '@/config/store';
import StatusTextGenecic from '@/components/UI/atoms/buttons/StatusTextGeneric';

const StyledModal = styled(Modal)`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const HeaderCell = styled(Typography)`
    font-size: 9.5px;
    font-weight: 700;
    padding: 8px 6px;
`;

const Cell = styled(Typography)`
    font-size: 9.5px;
    padding: 8px 6px;
    word-break: break-word;
`;

/** Ámbito de la nota. Lo resuelve el backend por el vínculo con la factura. */
type NoteScope = 'EXPENSE_ACCOUNT' | 'INVOICE';

interface IExpenseAccountNote {
    id: string;
    expenseAccountFolio: string;
    version: number;
    message: string;
    scope: NoteScope;
    invoiceReference: string | null;
    invoiceSerie: string | null;
    invoiceFolio: string | null;
    supplierTaxId: string | null;
    supplierName: string | null;
    type: string;
    origin: string;
    result: string | null;
    author: string | null;
    createdAt: string;
}

interface IProps {
    isModalOpen: boolean;
    closeModal: () => void;
    /** Renglón del listado: de aquí salen el agente y el pedimento de la ruta. */
    expenseAccount: {customAgent: string; customRequest: string; folio?: string} | null;
}

/**
 * Modal de notas del módulo interno (HU 2, criterio 6).
 *
 * Es un componente propio y no el modal "Ver mensajes" del Agente Aduanal, aunque la HU
 * pida que funcione "de forma similar": el criterio 6 exige una columna de **ámbito** que
 * ahí no existe, y reutilizar aquel obligaría a rebalancear su tabla, cambiando una
 * pantalla que hoy funciona y que no es de esta HU.
 *
 * Trae los datos con estado local en vez de pasar por el store: nadie más necesita verlos.
 */
const ModalExpenseAccountNotes: React.FC<IProps> = ({isModalOpen, closeModal, expenseAccount}) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const token = useSelector((state: RootState) => state.logins.accessToken);
    const [notes, setNotes] = useState<IExpenseAccountNote[]>([]);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);

    const fetchNotes = useCallback(async () => {
        if (!expenseAccount) return;
        setLoading(true);
        setFailed(false);
        try {
            const {data} = await axios.get(
                `${process.env.REACT_APP_BASE_URL}/v2/expense-accounts/validation/` +
                    `${expenseAccount.customAgent}/custom-request/` +
                    `${encodeURIComponent(expenseAccount.customRequest)}/notes`,
                {headers: {Accept: 'application/json', Authorization: 'Bearer ' + token}}
            );
            setNotes(data?.data ?? []);
        } catch (error) {
            // "Falló la consulta" y "no hay notas" son cosas distintas para el revisor:
            // en el primer caso puede reintentar, en el segundo no hay nada que esperar.
            setFailed(true);
            setNotes([]);
        } finally {
            setLoading(false);
        }
    }, [expenseAccount, token]);

    useEffect(() => {
        if (isModalOpen) fetchNotes();
    }, [isModalOpen, fetchNotes]);

    const scopeLabel = (note: IExpenseAccountNote) =>
        t(`expense_account_validation.notes.scopes.${note.scope}`);

    /** Serie y folio solo tienen sentido en una nota de factura (criterio 6). */
    const invoiceLabel = (note: IExpenseAccountNote) =>
        note.scope === 'INVOICE' ? note.invoiceReference ?? '-' : '-';

    const supplierLabel = (note: IExpenseAccountNote) =>
        note.supplierName ? `${note.supplierName} (${note.supplierTaxId})` : '-';

    return (
        <StyledModal open={isModalOpen} onClose={closeModal}>
            <Box
                sx={{
                    background: '#FFF',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: '10px',
                    width: '90vw',
                    maxHeight: '80vh',
                }}
            >
                <Nav>
                    <Typography
                        sx={{
                            fontSize: '18px',
                            fontFamily: theme.typography.fontFamily,
                            fontWeight: 700,
                            color: 'white',
                            marginLeft: '10px',
                        }}
                    >
                        {t('expense_account_validation.notes.title')}
                        {expenseAccount?.folio ? ` · ${expenseAccount.folio}` : ''}
                    </Typography>
                </Nav>

                <Box sx={{padding: '16px', overflowY: 'auto'}}>
                    {loading && (
                        <Box sx={{display: 'flex', justifyContent: 'center', padding: '30px'}}>
                            <CircularProgress size={28} />
                        </Box>
                    )}

                    {!loading && failed && (
                        <Typography sx={{fontSize: '12px', padding: '20px', textAlign: 'center'}}>
                            {t('expense_account_validation.notes.load_error')}
                        </Typography>
                    )}

                    {!loading && !failed && notes.length === 0 && (
                        <Typography sx={{fontSize: '12px', padding: '20px', textAlign: 'center'}}>
                            {t('expense_account_validation.notes.empty')}
                        </Typography>
                    )}

                    {!loading && !failed && notes.length > 0 && (
                        <>
                            <Grid container sx={{borderBottom: '1px solid #E5E5E5'}}>
                                <Grid item xs={2}>
                                    <HeaderCell>
                                        {t('expense_account_validation.notes.headers.scope')}
                                    </HeaderCell>
                                </Grid>
                                <Grid item xs={3}>
                                    <HeaderCell>
                                        {t('expense_account_validation.notes.headers.note')}
                                    </HeaderCell>
                                </Grid>
                                <Grid item xs={1.5}>
                                    <HeaderCell>
                                        {t('expense_account_validation.notes.headers.invoice')}
                                    </HeaderCell>
                                </Grid>
                                <Grid item xs={2}>
                                    <HeaderCell>
                                        {t('expense_account_validation.notes.headers.supplier')}
                                    </HeaderCell>
                                </Grid>
                                <Grid item xs={1}>
                                    <HeaderCell>
                                        {t('expense_account_validation.notes.headers.type')}
                                    </HeaderCell>
                                </Grid>
                                <Grid item xs={1}>
                                    <HeaderCell>
                                        {t('expense_account_validation.notes.headers.author')}
                                    </HeaderCell>
                                </Grid>
                                <Grid item xs={0.5}>
                                    <HeaderCell>
                                        {t('expense_account_validation.notes.headers.version')}
                                    </HeaderCell>
                                </Grid>
                                <Grid item xs={1}>
                                    <HeaderCell>
                                        {t('expense_account_validation.notes.headers.date')}
                                    </HeaderCell>
                                </Grid>
                            </Grid>

                            {notes.map((note) => (
                                <Grid
                                    container
                                    key={note.id}
                                    sx={{borderBottom: '1px solid #F4F4F4'}}
                                >
                                    <Grid item xs={2}>
                                        <Cell
                                            sx={{
                                                fontWeight: note.scope === 'INVOICE' ? 400 : 700,
                                            }}
                                        >
                                            {scopeLabel(note)}
                                        </Cell>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Cell>{note.message}</Cell>
                                    </Grid>
                                    <Grid item xs={1.5}>
                                        <Cell>{invoiceLabel(note)}</Cell>
                                    </Grid>
                                    <Grid item xs={2}>
                                        <Cell>{supplierLabel(note)}</Cell>
                                    </Grid>
                                    <Grid item xs={1}>
                                        <Cell>
                                            <StatusTextGenecic
                                                status={note.type}
                                                text={t(
                                                    `expense_account_validation.notes.types.${note.type}`
                                                )}
                                            />
                                        </Cell>
                                    </Grid>
                                    <Grid item xs={1}>
                                        <Cell>{note.author ?? '-'}</Cell>
                                    </Grid>
                                    <Grid item xs={0.5}>
                                        <Cell>{note.version}</Cell>
                                    </Grid>
                                    <Grid item xs={1}>
                                        <Cell>
                                            {new Date(note.createdAt).toLocaleString('es-MX')}
                                        </Cell>
                                    </Grid>
                                </Grid>
                            ))}
                        </>
                    )}
                </Box>
            </Box>
        </StyledModal>
    );
};

export default ModalExpenseAccountNotes;
